import { Ticket } from '../types';

// Platforms the phone-addressed batch-transfer endpoint accepts.
export type BatchTransferPlatform = 'DICE' | 'TAO';
export const BATCH_TRANSFER_PLATFORMS: BatchTransferPlatform[] = ['DICE', 'TAO'];

export const platformOf = (t: Ticket): string =>
  (t.external_platform_connection?.platform || t.platform || '').toUpperCase();

/**
 * The connected account a ticket lives in, the way the user recognises it.
 *
 * Shows every identifier the account has. DICE accounts are known by phone
 * number, every other platform by email, so that one leads; when both are on
 * file both are shown, e.g. "+44… · me@example.com" (FT-12).
 */
export const accountLabelOf = (t: Ticket, withIcons = false): string => {
  const conn = t.external_platform_connection;
  const phone = conn?.phone ? (withIcons ? `📱 ${conn.phone}` : conn.phone) : '';
  const email = conn?.email ? (withIcons ? `✉️ ${conn.email}` : conn.email) : '';
  const ordered = platformOf(t) === 'DICE' ? [phone, email] : [email, phone];
  return ordered.filter(Boolean).join(' · ') || 'Unknown account';
};

/**
 * Is this ticket in a state where a transfer can be attempted at all?
 *
 * Platform-agnostic on purpose — the same three conditions `TicketCard` uses to
 * show its per-ticket Transfer button. Which ENDPOINT can carry the transfer is
 * a separate question, answered by the two predicates below.
 */
export const isTransferable = (t: Ticket): boolean => {
  const status = (t.ticket_status || t.status || '').toString().toLowerCase();
  if (status !== 'active') return false;
  const meta = t.ticket_metadata as any;
  if (meta?.is_able_to_transfer === false) return false;
  return !!t.external_ticket_id;
};

export const isBatchTransferable = (t: Ticket): boolean =>
  BATCH_TRANSFER_PLATFORMS.includes(platformOf(t) as BatchTransferPlatform) && isTransferable(t);

// A batch call carries a single platform, so callers filter per platform.
export const isTransferableOn = (t: Ticket, platform: BatchTransferPlatform): boolean =>
  platformOf(t) === platform && isBatchTransferable(t);

/**
 * Transferable, but NOT by the batch endpoint — AXS, TIXR, anything new.
 *
 * `BatchTransferView` is addressed by recipient_phone, which only DICE and TAO
 * have, so AXS was left out of the event view's transfer buttons entirely and
 * the tickets looked untransferable. They are not: `ManualTransferView` handles
 * every platform and takes a LIST of ticket_ids, so one call moves a whole
 * event's worth. This predicate is what earns those tickets a button.
 */
export const isManualOnlyTransferable = (t: Ticket): boolean =>
  !BATCH_TRANSFER_PLATFORMS.includes(platformOf(t) as BatchTransferPlatform) && isTransferable(t);

/** Platforms in a group that can only go through the manual endpoint. */
export const manualOnlyPlatforms = (tickets: Ticket[]): string[] =>
  Array.from(new Set(tickets.filter(isManualOnlyTransferable).map(platformOf))).sort();

// DICE identity is the phone; TAO/AXS/TIXR identity is the email. Picking
// per-platform keeps every row of one platform labelled the same way, instead
// of falling back to whichever field happens to be populated.
//
// The whole number, not last-four: the ticket payload has always carried it,
// and two Dice accounts on the same carrier prefix are indistinguishable by
// their last four — which is precisely when a transfer goes to the wrong one.
export const accountLabel = (t: Ticket): string => {
  const conn = t.external_platform_connection;
  if (!conn) return 'Unknown account';
  const platform = (conn.platform || '').toUpperCase();
  const identity =
    platform === 'DICE'
      ? (conn.phone || conn.email)
      : (conn.email || conn.phone || '');
  return identity ? `${conn.platform} ${identity}` : `${conn.platform} (#${conn.id})`;
};

export const ticketTypeLabel = (t: Ticket): string => {
  const meta = t.ticket_metadata as any;
  return meta?.type?.name || t.ticket_type || 'GA';
};

export const ticketShortId = (t: Ticket): string => {
  const id = t.external_ticket_id || '';
  // TAO ticket UUIDs share their first and last segments across every ticket of
  // an event — only the middle groups discriminate, so the tail would render
  // identically for all of them.
  if (platformOf(t) === 'TAO') {
    const parts = id.split('-');
    if (parts.length >= 3) return parts.slice(1, 3).join('-');
    return id.slice(0, 8);
  }
  return id.length > 8 ? id.slice(-8) : id;
};

export const normalizePhone = (p: string) => p.replace(/[^\d+]/g, '');
export const isPhoneValid = (p: string) => /^\+\d{10,15}$/.test(normalizePhone(p));

export const eventNameOf = (t: Ticket): string => {
  const ev = typeof t.event === 'object' ? (t.event as any) : null;
  return ev?.name || t.event_name || 'Unknown event';
};

export const eventDateOf = (t: Ticket): string | null => {
  const ev = typeof t.event === 'object' ? (t.event as any) : null;
  return ev?.event_date_local || ev?.event_date_utc || t.event_date || null;
};

// The backend keys external_event_ids by lowercase platform ('dice', 'tao', …).
// Defaults to the ticket's own platform when the caller doesn't pin one.
export const externalEventIdOf = (t: Ticket, platform?: string): string | null => {
  const ev = typeof t.event === 'object' ? (t.event as any) : null;
  const key = (platform || platformOf(t)).toLowerCase();
  const ids = ev?.external_event_ids;
  if (ids) {
    const hit = ids[key] || ids[key.toUpperCase()];
    if (hit) return hit;
  }
  if ((t as any).external_event_id) return (t as any).external_event_id;
  return null;
};
